import { useEffect, useId, useRef, type CSSProperties, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import type { LandingSpinResult } from '@/lib/landing-roulette'
import { useLandingEmbedViewportPin } from '@/hooks/useLandingEmbedViewportPin'
import { isLegacyRouletteNoneLabel, ROULETTE_NONE_EMOJI } from '@/lib/rouletteNoneDisplay'
import { payloadNumber, rouletteCurrency } from './rouletteGeometry'
import { RouletteIcon } from './RouletteIcon'

type Props = {
  result: LandingSpinResult
  brandStyle: CSSProperties
  preview: boolean
  busy: boolean
  error: string | null
  returnFocusRef: RefObject<HTMLButtonElement | null>
  onClose: () => void
  onUseCoupon?: () => void
}

export function RouletteResult({
  result,
  brandStyle,
  preview,
  busy,
  error,
  returnFocusRef,
  onClose,
  onUseCoupon,
}: Props) {
  const dialog = useRef<HTMLDialogElement>(null)
  const viewportPin = useLandingEmbedViewportPin(true)
  const id = useId()
  const won = result.prize_type !== 'none'
  const payload = result.prize_payload ?? {}
  const percent = payloadNumber(payload.discount_percent)
  const amount = payloadNumber(payload.discount_amount)
  const pix = payloadNumber(payload.amount)
  const quantity = payloadNumber(payload.quantity)
  const customLabel =
    typeof payload.label === 'string' &&
    !isLegacyRouletteNoneLabel(payload.label) &&
    payload.label !== ROULETTE_NONE_EMOJI
      ? payload.label
      : null
  const title = preview ? 'Teste concluído!' : won ? 'Parabéns!' : 'Não foi dessa vez'
  const value =
    result.prize_type === 'coupon'
      ? percent
        ? `${percent}%`
        : amount
          ? `R$ ${rouletteCurrency(amount)}`
          : 'Cupom especial'
      : result.prize_type === 'pix'
        ? pix !== null
          ? `R$ ${rouletteCurrency(pix)}`
          : 'Prêmio PIX'
        : result.prize_type === 'lucky_number'
          ? quantity !== null
            ? `+${quantity}`
            : 'Números da sorte'
          : customLabel || 'A sorte continua'

  useEffect(() => {
    const element = dialog.current
    const previous =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const returnFocusElement = returnFocusRef.current ?? previous
    const overflow = document.body.style.overflow
    element?.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      element?.close()
      document.body.style.overflow = overflow
      returnFocusElement?.focus({ preventScroll: true })
    }
  }, [returnFocusRef])

  return createPortal(
    <dialog
      ref={dialog}
      className={`roulette-result${won ? ' roulette-result--won' : ''}`}
      style={{
        ...brandStyle,
        ...(viewportPin
          ? {
              top: viewportPin.top,
              bottom: 'auto',
              marginBlock: 16,
              maxHeight: `calc(${viewportPin.height}px - 32px)`,
            }
          : {}),
      }}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      onCancel={(event) => {
        event.preventDefault()
        if (!busy) onClose()
      }}
    >
      <div className="roulette-result-art" aria-hidden="true">
        {won && (
          <div className="roulette-confetti">
            {Array.from({ length: 32 }, (_, i) => (
              <i
                key={i}
                style={
                  {
                    '--x': `${((i * 47) % 280) - 140}px`,
                    '--y': `${80 + ((i * 31) % 180)}px`,
                    '--r': `${i * 137}deg`,
                    '--delay': `${(i % 8) * 0.04}s`,
                    '--confetti-color':
                      i % 3 === 0
                        ? '#e6bc65'
                        : i % 3 === 1
                          ? 'var(--wheel-brand-primary)'
                          : 'var(--wheel-brand-light)',
                  } as CSSProperties
                }
              />
            ))}
          </div>
        )}
        <div className="roulette-result-medal">
          <RouletteIcon kind={won ? 'gift' : 'none'} />
        </div>
      </div>
      <div className="roulette-result-body">
        <p className="roulette-eyebrow">
          {preview
            ? 'PRÉVIA DA CAMPANHA'
            : won
              ? 'ESSE MOMENTO É SEU'
              : 'OBRIGADO POR PARTICIPAR'}
        </p>
        <h2 id={`${id}-title`}>{title}</h2>
        <p id={`${id}-description`} className="roulette-result-description">
          {preview
            ? 'Resultado do giro simulado'
            : won
              ? 'Você acaba de ganhar na roleta.'
              : 'Este giro terminou sem prêmio.'}
        </p>
        <div className="roulette-result-prize">
          <strong>{value}</strong>
          {result.prize_type === 'coupon' && (
            <span>de desconto na sua próxima compra</span>
          )}
          {result.prize_type === 'pix' && <span>em PIX</span>}
          {result.prize_type === 'lucky_number' && (
            <span>{quantity === 1 ? 'número da sorte' : 'números da sorte'}</span>
          )}
        </div>
        {result.prize_type === 'coupon' && (
          <>
            {percent !== null && amount !== null && (
              <p className="roulette-result-note">
                Desconto em valor: R$ {rouletteCurrency(amount)}
              </p>
            )}
            {typeof payload.description === 'string' && (
              <p className="roulette-result-description">{payload.description}</p>
            )}
            {typeof payload.code === 'string' && (
              <div className="roulette-coupon">
                <span>SEU CUPOM</span>
                <strong>{payload.code}</strong>
              </div>
            )}
            {payloadNumber(payload.min_purchase) !== null && (
              <p className="roulette-result-note">
                Compra mínima de R${' '}
                {rouletteCurrency(payloadNumber(payload.min_purchase)!)}
              </p>
            )}
            {typeof payload.valid_until === 'string' && (
              <p className="roulette-result-note">
                Válido até{' '}
                {payload.valid_until.slice(0, 10).split('-').reverse().join('/')}
              </p>
            )}
          </>
        )}
        {result.prize_type === 'lucky_number' &&
          !preview &&
          Array.isArray(payload.lucky_numbers) && (
            <>
              <p className="roulette-result-note">
                Seus números estão reservados. Vá até Meus números e toque em Resgatar
                para ativá-los.
              </p>
              <div className="roulette-result-numbers">
                {payload.lucky_numbers
                  .filter((number): number is string => typeof number === 'string')
                  .join(' · ')}
              </div>
            </>
          )}
        {error && (
          <p className="roulette-notice roulette-notice--error" role="alert">
            {error}
          </p>
        )}
        <div className="roulette-result-actions">
          {onUseCoupon && (
            <button
              type="button"
              className="roulette-action"
              onClick={onUseCoupon}
              disabled={busy}
              autoFocus
            >
              {busy ? 'Abrindo compra…' : 'Usar cupom'}
              <RouletteIcon kind="arrow" />
            </button>
          )}
          <button
            type="button"
            className={
              onUseCoupon ? 'roulette-action roulette-action--quiet' : 'roulette-action'
            }
            onClick={onClose}
            disabled={busy}
            autoFocus={!onUseCoupon}
          >
            Fechar
          </button>
        </div>
        {won && !preview && (
          <p className="roulette-result-footnote">
            O resultado está salvo em Meus prêmios da roleta.
          </p>
        )}
      </div>
    </dialog>,
    document.body
  )
}
